import { createElement } from '@qdabra/build-xml';
import { trimEnd, trimStart } from '../../../utility/string.helpers';
import {
    paramChild,
    paramClear,
    paramDsName,
    paramFullPathAttributeName,
    paramLastModifiedLocalAttributeName,
    paramMode,
    paramParent,
    paramPath,
} from '../constants/parameterNames';
import {
    typeBoolean,
    typeDataSource,
    typeOption,
    typeXPath,
} from '../constants/parameterTypes';
import { failure, success } from '../helpers/commandResult';
import { handleError } from '../helpers/errorHandling';
import {
    getBoolParamValue,
    getParamValue,
} from '../helpers/parameter.accessors';
import { getDataSource } from '../helpers/qRules.dataSource.helpers';
import {
    getValidNodeSet,
    removeChildren,
} from '../helpers/qRules.node.helpers';

const description =
    'Lists all of the files or directories in a file system directory and places the results in a repeating group. Not available in all FormsViewer deployments.';

const FILE_MODE_FILE = 'file';
const FILE_MODE_DIRECTORY = 'directory';

const optionalParameters = {
    [paramMode]: {
        description:
            'Indicates whether to list files or directories. Possible values are file and directory. Default is file.',
        type: typeOption,
        options: [
            {
                name: FILE_MODE_FILE,
                value: FILE_MODE_FILE,
            },
            {
                name: FILE_MODE_DIRECTORY,
                value: FILE_MODE_DIRECTORY,
            },
        ],
    },
    [paramDsName]: {
        description: 'Data source where the results should be deposited',
        type: typeDataSource,
    },
    [paramClear]: {
        name: paramClear,
        description:
            '(boolean, defaults to true) Indicates whether to clear any previously present child nodes on success.',
        type: typeBoolean,
    },
    [paramFullPathAttributeName]: {
        description:
            'Attribute on the child node to store the full file or directory path.',
        type: typeXPath,
        dsParentName: paramDsName,
        relativeTo: paramParent,
    },
    [paramLastModifiedLocalAttributeName]: {
        description:
            "Attribute on the child node to store the last modified date of the file or directory. Uses the server's local time zone.",
        type: typeXPath,
        dsParentName: paramDsName,
        relativeTo: paramParent,
    },
};

const requiredParameters = {
    [paramPath]: {
        description: 'Path to the directory to list.',
    },
    [paramParent]: {
        description: 'Parent node where the results should be deposited.',
        type: typeXPath,
        dsParentName: paramDsName,
    },
    [paramChild]: {
        description: 'Name of the child node used to list results.',
        type: typeXPath,
        dsParentName: paramDsName,
        relativeTo: paramParent,
    },
};

const errorInvalidChild = 'Invalid child node name: ';
const errorNoParent = 'Unable to find destination node.';

function validateListingMode(mode) {
    const supportedModes = [FILE_MODE_FILE, FILE_MODE_DIRECTORY];

    if (!supportedModes.includes(mode.toLowerCase())) {
        throw new Error(
            `Invalid mode value: ${mode}. Allowed values are 'file' and 'directory'.`,
        );
    }
}

function findNamespace(node, prefix) {
    let currentNode = node;

    while (currentNode) {
        const namespace = currentNode.getAttribute(`xmlns:${prefix}`);

        if (namespace) {
            return namespace;
        }

        currentNode = currentNode.parentNode;
    }

    throw new Error(`Cannot find namespace for prefix ${prefix}`);
}

function getNamespace(node, child) {
    if (child.indexOf(':') < 0) {
        return null;
    }

    const splitChild = child.split(':');

    if (splitChild.length !== 2) {
        throw new Error(errorInvalidChild + child);
    }

    return findNamespace(node, splitChild[0]);
}

const setAttribute = (targetElement, referenceNode, attrName, value) => {
    const attrActualName = trimStart(attrName, '@');
    const attrNs = getNamespace(referenceNode, attrActualName);

    if (attrNs) {
        targetElement.setAttributeNS(attrNs, attrActualName, value);
    } else {
        targetElement.setAttribute(attrActualName, value);
    }
};

const createNodesAndSetPathAsync = async (
    node,
    items,
    dom,
    childNs,
    childPath,
    fullPathAttrName,
    lastModifiedLocalAttrName,
) => {
    const childNodes = items.map((item) => {
        const childNode = createElement(dom, childNs, childPath, {
            text: item.Name,
        });

        if (fullPathAttrName) {
            setAttribute(childNode, node, fullPathAttrName, item.FullPath);
        }
        if (lastModifiedLocalAttrName) {
            setAttribute(
                childNode,
                node,
                lastModifiedLocalAttrName,
                item.LastModifiedLocal,
            );
        }

        return childNode;
    });

    await node.appendChildrenAsync(childNodes);
};

const processItemsAsync = async (
    dom,
    parents,
    items,
    childPath,
    fullPathAttrName,
    lastModifiedLocalAttrName,
    clear,
) => {
    const parentNode = parents[0].getNode();
    const childNs = getNamespace(parentNode, childPath);

    for (const node of parents) {
        if (clear) {
            removeChildren(node);
        }

        await createNodesAndSetPathAsync(
            node,
            items,
            dom,
            childNs,
            childPath,
            fullPathAttrName,
            lastModifiedLocalAttrName,
        );
    }

    return items.length;
};

const executeListDirectoryAsync = async (qfs, dom, parents, cmdParams) => {
    validateListingMode(cmdParams.mode);

    const data = await qfs.listDirectoryAsync(cmdParams.path, cmdParams.mode);

    if (!data.success) {
        return failure(
            (data.error && data.error.Message) || 'Unexpected error',
        );
    }

    const count = await processItemsAsync(
        dom,
        parents,
        data.data,
        cmdParams.child,
        cmdParams.fullPathAttrName,
        cmdParams.lastModifiedLocalAttrName,
        cmdParams.clear,
    );

    return success(count);
};

//Format path, to remove slashes
//Case 35425 - no error if child is not valid
const getCommandParams = (parameters) => ({
    path: getParamValue(parameters, paramPath),
    parent: trimEnd(getParamValue(parameters, paramParent), '/'),
    child: trimStart(getParamValue(parameters, paramChild), '/'),
    fullPathAttrName: getParamValue(parameters, paramFullPathAttributeName),
    lastModifiedLocalAttrName: getParamValue(
        parameters,
        paramLastModifiedLocalAttributeName,
    ),
    mode: getParamValue(parameters, paramMode, FILE_MODE_FILE),
    dsName: getParamValue(parameters, paramDsName),
    clear: getBoolParamValue(parameters, paramClear, true),
});

const executeAsync = async ({ api, dataSources, parameters }) => {
    const cmdParams = getCommandParams(parameters);

    const dom = getDataSource(dataSources, cmdParams.dsName);
    const parents = getValidNodeSet(dom, cmdParams.parent);

    if (!parents.length) {
        return failure(errorNoParent);
    }

    return executeListDirectoryAsync(api, dom, parents, cmdParams).catch(
        handleError,
    );
};

const ListDirectory = {
    description,
    executeAsync,
    optionalParameters,
    requiredParameters,
};

export default ListDirectory;
